import { useContext, useEffect, useMemo, useState } from 'react';
import { issueOptions, queryKeys } from '@shared/api';
import * as z from 'zod';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';

import {
	Article,
	clone,
	Issue,
	USER_ROLE,
	ISSUE_LANG_KEY,
	Style,
	LangEnum,
} from '@/constants';
import config from '@/config';

import { useIssueNotFound, useServiceWorker } from '@shared/lib/hooks';
import { AppContext } from '@/app.context';
import { buildStyles } from '@/lib/article-renderer/core/style-builder';
import { Styles } from '@/lib/article-renderer/core/styles';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('issue.hooks');

export const ArticlesSortKeys = z
	.enum(['RELEASE_DATE', 'CREATED', 'UPDATED', 'INDEX'])
	.optional();

export const issueSearchSchema = z.object({
	lang: LangEnum,
	limit: z.number().optional().catch(undefined),
	sortKey: ArticlesSortKeys.catch(undefined),
	reverse: z.boolean().optional(),
	// Token auth's credential (`app/routing/token-auth-guard.ts`'s
	// `tokenAuthGuard`, the default `beforeLoad` gate every issue route
	// falls back to unless a template supplies its own). Declared here so
	// it survives `validateSearch` instead of relying on unknown-param
	// passthrough (verified real, see the topic file, but still not
	// something to lean on by omission) -- read from the guard's own
	// `beforeLoad` opts, not a hook, since this same field is also relevant
	// on several other route ids across templates (home, issues,
	// collections/$id) that each declare it in their own schema too.
	token: z.string().optional(),
});

export type IssueSearch = z.infer<typeof issueSearchSchema>;

/**
 * React hook that load data from navigation
 *
 */
export function useLoad(): NavigationResponse {
	const { id, articleId } = useParams({ strict: false });
	const issueId = id;
	const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
	const { issue, isUnavailableOffline } = useIssue(`${issueId}`);

	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!issue.articles) {
			return;
		}
		let art = issue.articles[0];
		for (const article of issue.articles) {
			if (article.id === articleId || article.slug === articleId) {
				art = article;
				break;
			}
		}
		setCurrentArticle(art);
	}, [issue, articleId]);

	return {
		issue,
		currentArticle,
		setCurrentArticle,
		isUnavailableOffline,
	};
}

/**
 * React hook that returns the details of the issue
 *
 * @param id - Props that gets the issue by it's id
 *
 */
export function useIssue(id: string): GetIssueResponse {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	// Deliberately `strict: false`, not route-scoped: `useIssue` is also
	// called from `templates/{poets,padi}/home/magazines/magazines.tsx` on
	// each template's home route, not just from `/issues/$id` -- there's no
	// one route id this hook can bind to. Narrowly typed to the three
	// fields actually read, since the home-route callers' search shape
	// doesn't otherwise match `IssueSearch`.
	const search: Pick<IssueSearch, 'limit' | 'sortKey' | 'reverse'> =
		useSearch({ strict: false });

	const queryKey = queryKeys.issue(id, {
		limit: search?.limit,
		sortKey: search?.sortKey,
		reverse: search?.reverse,
	});
	const { onlineStatus: isOnline, db } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	// Computed independently of the query below, same synchronous
	// getQueryData check as before -- `useSuspenseQuery` doesn't support
	// `enabled`, so this no longer *skips* the fetch (`fetchIssue`'s own
	// offline branch already resolves to `{ issue: null }` without a
	// network attempt when this is true, so nothing is lost by not
	// skipping), but the UI still needs this exact signal to show
	// "unavailable offline" rather than the not-found path.
	const isUnavailableOffline =
		!isOnline && queryClient.getQueryData(queryKey) === undefined;

	// `issueOptions` (the same factory the `issue`/`issues` route loaders
	// call via `ensureQueryData`) -- fetch logic lives in `shared/api/
	// queries.ts` now, not duplicated here. `useSuspenseQuery`, not
	// `useQuery`: the route loader already ensured this exact query before
	// this component ever mounted (see `route-catalog.tsx`), so this never
	// actually suspends for the route-backed case -- topic 08's "components
	// render data, they don't fetch it" loader item. The direct,
	// non-route-backed callers above suspend for real; their own render
	// sites wrap them in a local `<Suspense>` boundary for exactly that.
	const { data } = useSuspenseQuery({
		...issueOptions(id, {
			isOnline,
			isAdmin,
			ttlMinutes: config.app?.cache?.ttl?.getIssue,
			limit: search?.limit,
			sortKey: search?.sortKey,
			reverse: search?.reverse,
			onSessionExpired: () => navigate({ to: '/' }),
		}),
		networkMode: 'always',
		retry: isOnline ? 3 : 0,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		// buildStyles moved in here from a bare `if` in the render body, which
		// mutated `issue.styles` in place on *every* render -- rebuilding
		// already-built styles each time, and mutating state during render.
		// Doing it once, where the issue is first derived from the query data,
		// is equivalent and does the work once per fetch instead.
		const next = clone(data.issue);
		if (next?.styles) {
			next.styles = buildStyles(next.styles);
			// Persisted here, not per-favorite-click: this is the one place
			// every article's style is guaranteed resolved (inheritance
			// merged) and available, regardless of whether the reader ever
			// favorites anything. Without this, `db.getStyle(...)` -- what
			// the Library section resolves a favorited article's style
			// through -- had nothing to ever find, since nothing else
			// populated the `styles` table. `saveStyles` is additive (see
			// its own comment): loading a second issue must not evict the
			// first one's already-favorited articles' styles.
			if (db) {
				db.saveStyles(next.styles).catch((error: unknown) => {
					logger.error('Failed to save issue styles', error);
				});
			}
		}
		setIssue(next);
	}, [data, db]);

	return {
		isUnavailableOffline,
		issue,
	};
}

interface GetIssueResponse {
	isUnavailableOffline: boolean;
	issue?: Issue | null;
}

interface NavigationResponse extends GetIssueResponse {
	currentArticle: Article | null | undefined;
	setCurrentArticle: (article: Article) => void;
}

export function useStylesMap(issue?: Issue | null): Map<string, Style> | null {
	return useMemo(() => {
		if (!issue) return null;
		const { styles } = issue;
		if (!styles) return new Map();

		return new Styles(issue.styles).styles;
	}, [issue]);
}

/**
 * React hook that load data from navigation
 *
 */
/**
 * Derives the current article's `<title>`/description for `<DocumentHead>`.
 *
 * Was an effect that wrote `document.title` and mutated the description meta
 * tag directly, with no cleanup -- see shared/ui/document-head for why that
 * was replaced. Pure derivation now; rendering the values is the caller's job.
 *
 * `fixToUtf8` is applied to the title only, exactly as before: it repairs
 * mojibake/entity-encoded text from the CMS, and this hook is the only place
 * that ever did so (pages/article's equivalent deliberately does not -- see
 * its own useTitle).
 */
export function useTitle(args: TitleArgs): DocumentMeta {
	const { article, lang } = args;

	return useMemo(() => {
		if (!article || !lang) {
			return {};
		}
		const { name, description } = article;
		return {
			title: name?.[lang] ? fixToUtf8(name[lang]) : undefined,
			description: description ? description : undefined,
		};
	}, [article, lang]);
}

export interface DocumentMeta {
	title?: string;
	description?: string;
}

/**
 * Safely converts and repairs any broken, scrambled, or entity-encoded
 * dynamic text into clean UTF-8 Arabic text.
 * @param {string} text - The raw dynamic text string.
 * @returns {string} - Clean, properly encoded string.
 */
function fixToUtf8(text: string) {
    if (!text || typeof text !== 'string') return '';

    // Step 1: Decode HTML numerical entities (e.g., &#1593;&#1606;)
    if (text.includes('&')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        text = doc.body.textContent || doc.documentElement.textContent || text;
    }

    // Step 2: Fix Mojibake/Scrambled text (e.g., "ÚäæÇä" or "Ø¹Ù†ÙˆØ§Ù†")
    // This safely un-mangles bytes forced into Windows-1252 / ISO-8859-1 back to UTF-8
    try {
        const latinBytes = new Uint8Array(text.split('').map(char => char.charCodeAt(0)));
        const decodedText = new TextDecoder('utf-8', { fatal: true }).decode(latinBytes);
        
        // If the output doesn't contain fallback question mark blocks, return the healed string
        if (!decodedText.includes('')) {
            text = decodedText;
        }
    } catch {
        // If it throws an error, the text wasn't encoded as standard Mojibake.
        // We catch it and proceed with the text as-is.
    }

    return text;
}


interface TitleArgs {
	article: Article | null | undefined;
	lang: string;
}

/**
 * React hook that control font
 *
 */
/**
 * React hook that runs when an article change
 *
 */
export function useArticle({ issue, article }: ArticleArgs) {
	const navigate = useNavigate();
	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!article) {
			return;
		}

		let hash: undefined | string;

		if (article.hash) {
			hash = article.hash;
		} else if (window.location.hash) {
			hash = window.location.hash;
		}

		navigate({
			replace: true,
			to: '/issues/$id/$articleId',
			params: {
				id: issue.slug || issue.id,
				articleId: article.slug || article.id,
			},
			search: (prevSearch: IssueSearch) => ({ ...prevSearch }),
			hash,
		});

		scrollTop(article.id);
	}, [issue, article, navigate]);
	useAutoPlayVideos({ article });
}

interface ArticleArgs {
	issue?: Issue | null | undefined;
	article: Article | null;
}

function useAutoPlayVideos({ article }: ArticleArgs) {
	useEffect(() => {
		if (!article) {
			return;
		}
		const articleNode = document.getElementById(`article-${article.id}`);
		if (!articleNode) {
			return;
		}

		/*Check for video elements*/
		const videoNodes = articleNode.querySelectorAll('video');
		for (const node of videoNodes) {
			if (node.hasAttribute('autoplay')) {
				node.play();
			}
		}
	}, [article]);
}

/**
 * React hook that download images in the background
 *
 */
export function useDownloadAssets(args: DownloadImagesArgs) {
	const { isReady, sendMessage } = useServiceWorker();
	const { issue } = args;
	useEffect(() => {
		if (!isReady) return; // Service worker is not ready
		if (!issue) return; // Issue is not set
		if (!issue.articles) return; // Issue do not have any articles

		const images = [
			...issue.articles.reduce(reduceArticleImages, new Set<string>()),
		];

		sendMessage({
			type: 'DOWNLOAD_BACKGROUND_IMAGES',
			payload: {
				images,
				IssueID: issue.id,
			},
		});
	}, [issue, isReady, sendMessage]);
}

interface DownloadImagesArgs {
	issue: Issue | null;
}

/**
 * React hook that handles the article language
 *
 */

export function useArticleLang({
	article,
	languages,
	setLanguage,
}: ArticleLangArgs): void {
	const { lang } = useContext(AppContext);
	const navigate = useNavigate();
	const search: IssueSearch = useSearch({ strict: false });
	useEffect(() => {
		if (!article || !languages.size) return;
		const issueLang = getIssueLang(lang);
		if (search?.lang) {
			if (languages.has(search.lang)) {
				localStorage.setItem(ISSUE_LANG_KEY, search.lang);
				setLanguage(search.lang);
				return;
			}

			// If the lang is passed but is not available i remove it

			// `replace: true` matters here the same way it does in
			// `useArticle` above (and `templates/morningstar/home/home.tsx`'s
			// mode-sync effect): this fires automatically as a correction,
			// not from a user click, so without it every correction pushes a
			// fresh history entry instead of amending the current one --
			// corrupting the back-stack for a reader who never asked to
			// navigate anywhere.
			navigate({
				to: '.',
				replace: true,
				search: (prevSearch: IssueSearch) => {
					delete prevSearch.lang;
					return prevSearch;
				},
			});
			setLanguage(issueLang);
			return;
		}
		setLanguage(issueLang);
	}, [article, search, languages, navigate, setLanguage, lang]);
}

interface ArticleLangArgs {
	article: Article | null | undefined;
	languages: Set<string>;
	setLanguage: React.Dispatch<React.SetStateAction<string>>;
}

function getIssueLang(lang: string): string {
	if (!localStorage.getItem(ISSUE_LANG_KEY)) {
		localStorage.setItem(ISSUE_LANG_KEY, lang);
	}

	return `${localStorage.getItem(ISSUE_LANG_KEY)}`;
}

// `article.components` is a JSON scalar with no schema shape (see
// constants.ts's Article doc comment) -- this is the minimal recursive
// shape this background-image walk actually reads, not the full CMS
// component union (see components/article/virtual/component.type.ts for
// that).
interface ComponentImageNode {
	components?: Array<ComponentImageNode>;
	columns?: Array<Array<ComponentImageNode>>;
	images?: Array<{ imageurl?: string }>;
	imageurl?: string;
}

function reduceArticleImages(acc: Set<string>, article: Article) {
	const components = article.components as
		| Array<ComponentImageNode>
		| null
		| undefined;
	if (!components) {
		// Was `article.components = null` -- a gratuitous write to a shared
		// article object (and a no-op semantically: this branch only runs when
		// `components` is already falsy).
		return acc;
	}
	return components.reduce(reduceComponentImages, acc);
}

function reduceComponentImages(acc: Set<string>, component: ComponentImageNode) {
	const { components, columns, images, imageurl } = component;
	if (components) {
		acc = components.reduce(reduceComponentImages, acc);
	}
	// `columns`/`tabs` components nest their children one level deeper than
	// a plain `components` array: `columns` is an array of columns, each
	// itself an array of components (see Columns.tsx's `columns` prop /
	// Tabs.tsx's `columns[tabIndex]`) -- without this, every image nested
	// inside a column or tab was invisible to the background prefetcher.
	if (columns) {
		acc = columns.reduce(
			(acc: Set<string>, column: Array<ComponentImageNode>) =>
				column.reduce(reduceComponentImages, acc),
			acc,
		);
	}
	if (imageurl) {
		acc.add(imageurl);
	}
	// Gallery components carry their images as an array of
	// { imageurl, caption } objects rather than a single `imageurl`.
	if (images) {
		for (const image of images) {
			if (image?.imageurl) {
				acc.add(image.imageurl);
			}
		}
	}
	return acc;
}

/*function reduceVideos(acc: Set<string>, article: any) {
	return article.components.reduce(reduceVideo, acc);
}

function reduceVideo(acc: Set<string>, component: any) {
	const { components, moviepath } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}

	if (moviepath) {
		acc.add(moviepath);
	}
	return acc;
}*/



function scrollTop(articleId: string) {
	const el = document.getElementById(`article-${articleId}`);
	if (!el) {
		return;
	}

	const options: ScrollToOptions = {
		top: 0,
		left: 0,
		behavior: 'instant',
	};

	el.scroll(options);
}
